import React from "react";
import { Box } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import AltNames from "../../MenuName/AltNames";
import QtyStepper from "./QtyStepper";
import { AiTag, LikedLine, MostLikedBadge, priceLabel } from "./MenuItemCard";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, SHADOW, TYPE } from "../../../utils/designTokens";
import { resizedImage, resizedSrcSet } from "../../../utils/resizedImage";

/**
 * A "Most ordered" photo card: 4:3 photo, optional "#1 most liked" badge,
 * name, price and liked %. A white "+ Add" pill sits on the photo and turns
 * into a −/+ stepper once the dish is in the bag.
 *
 * `item` is the mostOrderedItems row (its own photo, stats); `food` is the
 * matching menu item, which carries the sizes and prices actually charged.
 */
function MostOrderedCard({ item, food, quantity = 0, onOpen, onAdd, onRemove, currencySymbol = "$", langDisplay = "NONE" }) {
  const name = food?.title || item.title;
  const image = item.image || food?.image || "";
  const soldOut = !!food?.is86ed;
  const rank = item.mostLikedRank ?? food?.mostLikedRank;
  const liked = item.likedPercent ?? food?.likedPercent;
  const votes = item.voteCount ?? food?.voteCount;
  const price = food ? priceLabel(food, currencySymbol) : item.price != null ? `${currencySymbol}${Number(item.price).toFixed(2)}` : null;

  return (
    <Box component="article" sx={{ position: "relative", minWidth: 0, opacity: soldOut ? 0.6 : 1 }}>
      <Box
        sx={{
          position: "relative",
          aspectRatio: "4 / 3",
          borderRadius: RADIUS.menuCard,
          overflow: "hidden",
          bgcolor: C.surface,
          border: `1px solid ${C.border}`,
        }}
      >
        {image ? (
          <Box
            component="img"
            src={resizedImage(image, 480)}
            srcSet={resizedSrcSet(image, [480, 800])}
            sizes="(max-width: 599px) 100vw, (max-width: 899px) 50vw, 25vw"
            alt={name}
            loading="lazy"
            decoding="async"
            sx={{
              position: "absolute",
              inset: 0,
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              filter: soldOut ? "grayscale(100%)" : "none",
              transition: "transform .3s ease",
              "article:hover &": { transform: soldOut ? "none" : "scale(1.03)" },
            }}
          />
        ) : (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted }}>
            <RestaurantRoundedIcon aria-hidden="true" sx={{ fontSize: 36 }} />
          </Box>
        )}
        {rank && rank <= 3 && <MostLikedBadge rank={rank} sx={{ position: "absolute", top: 10, left: 10, bgcolor: C.bg, color: C.ink, boxShadow: SHADOW.button }} />}
        {/* mostOrderedItems sends the AI picture as `image` when the dish has
            no photo of its own (backend: imageUrl || aiImageUrl, the same rule
            as the menu), so the menu item's flag says what is on show. */}
        {food?.isAiImage && (
          <AiTag onPhoto sx={{ position: "absolute", bottom: 10, left: 10, pointerEvents: "none" }}>AI image</AiTag>
        )}
        {food && !soldOut && (
          // Above the card's stretched button, so it is its own target.
          <Box sx={{ position: "absolute", right: 10, bottom: 10, zIndex: 1 }}>
            {quantity > 0 ? (
              <QtyStepper quantity={quantity} onAdd={onAdd} onRemove={onRemove} itemName={name} floating />
            ) : (
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd();
                }}
                aria-label={`Add ${name}`}
                sx={{
                  ...BUTTON_RESET,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  height: 40,
                  pl: 1.25,
                  pr: 1.75,
                  borderRadius: RADIUS.pill,
                  bgcolor: C.bg,
                  color: C.ink,
                  boxShadow: SHADOW.button,
                  fontFamily: FONT_BODY,
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "background-color .15s ease",
                  "&:hover": { bgcolor: C.surface },
                  ...FOCUS_RING,
                }}
              >
                <AddRoundedIcon sx={{ fontSize: 20 }} />
                Add
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ pt: 1.25 }}>
        <Box
          component="button"
          type="button"
          onClick={onOpen}
          disabled={soldOut || !food}
          sx={{
            ...BUTTON_RESET,
            ...TYPE.itemName,
            color: C.ink,
            textAlign: "left",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            cursor: soldOut || !food ? "default" : "pointer",
            "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: RADIUS.menuCard },
            "&:focus-visible": { outline: "none" },
            "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: 2 },
          }}
        >
          {name}
          <AltNames alt1={item.altName1 || food?.altName1} alt2={item.altName2 || food?.altName2} displayMode={langDisplay} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 0.25, flexWrap: "wrap" }}>
          {price && (
            <Box component="span" sx={{ ...TYPE.meta, fontSize: 14, color: C.ink, fontVariantNumeric: "tabular-nums" }}>
              {price}
            </Box>
          )}
          {liked != null && <LikedLine percent={liked} votes={votes} />}
        </Box>
        {soldOut && (
          <Box sx={{ mt: 0.25, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.redLink }}>Temporarily unavailable</Box>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(MostOrderedCard);
